import { useCallback, useEffect, useRef } from "react";

import Account from "byzantine/src/Account";
import { MAX_PAGE_SIZE } from "byzantine/src/dbbl/businessLogic/entities/accounts/consts";
import { useRequestWithFeedback } from "byzantine/src/dbbl/composites/request/request.hooks";
import { useObjectMemo } from "byzantine/src/dbbl/hooks/useObjectMemo.hook";

type RefreshExternalAccountsData = {
  accounts: API.AnyAccount[];
};

export const STALE_MX_SYNC_THRESHOLD_MS = 3 * 60 * 60 * 1000;

const isStaleDisconnectedMxAccount = (account: Account): boolean => {
  if (account.sync_status !== "disconnected") {
    return false;
  }

  if (account.synced_with !== "mx") {
    return false;
  }

  if (!account.updated_from_source_at) {
    return true;
  }

  return (
    Date.now() - new Date(account.updated_from_source_at).getTime() >
    STALE_MX_SYNC_THRESHOLD_MS
  );
};

export default function useRefreshExternalAccounts() {
  const { send, loading, status } =
    useRequestWithFeedback<RefreshExternalAccountsData>();

  const refreshExternalAccounts = useCallback(
    (onSuccess: (accounts: Account[]) => void) => {
      send({
        action: {
          url: `accounts/?per_page=${MAX_PAGE_SIZE}&sync_external_accounts=true`,
          options: {
            method: "GET",
            timeoutMilliseconds: 90 * 1000,
          },
        },
        onData: (data) => {
          onSuccess(
            data.accounts
              .filter((account) => account.source === "external")
              .map((account) => Account.deserialize(account))
          );
        },
      });
    },
    [send]
  );

  return useObjectMemo({
    refreshExternalAccounts,
    loading,
    error: status === "error",
  });
}

export function useRefreshStaleExternalAccounts({
  externalAccounts,
  onRefresh,
}: {
  externalAccounts: Account[];
  onRefresh: (accounts: Account[]) => void;
}) {
  const { refreshExternalAccounts } = useRefreshExternalAccounts();
  const hasTriggeredSyncRefresh = useRef(false);

  useEffect(() => {
    if (hasTriggeredSyncRefresh.current) {
      return;
    }

    if (!externalAccounts.some(isStaleDisconnectedMxAccount)) {
      return;
    }

    hasTriggeredSyncRefresh.current = true;
    refreshExternalAccounts(onRefresh);
  }, [externalAccounts, onRefresh, refreshExternalAccounts]);
}
